/**
 * Keycloak Resource Owner Password Credentials for development IAM only.
 * Requires VITE_AUTH_MODE=dev_keycloak_password and a public client with Direct Access Grants.
 * Never stores username or password.
 */

import { decodeJwtPayload } from './jwtPayload';
import { getKeycloakClientId, getKeycloakIssuer } from './keycloakClient';
import { getOidcConfiguration } from './oidcDiscovery';
import {
  applyLoginSessionOptions,
  markCookieSessionActive,
  setDevAuthSession,
  type LoginSessionOptions,
} from './tokenStore';
import { isCookieAuthMode } from './cookieAuthMode';

export function isPasswordGrantConfigReady(): boolean {
  return Boolean(getKeycloakIssuer() && getKeycloakClientId());
}

export type PasswordLoginResult =
  | { status: 'success' }
  | { status: 'error'; message: string };

function mapPasswordLoginError(payload: Record<string, unknown>, status: number): string {
  const error = String(payload.error ?? '').trim().toLowerCase();
  const description = String(payload.error_description ?? '').trim().toLowerCase();
  const combined = `${error} ${description}`.trim();

  if (
    error === 'invalid_grant' ||
    combined.includes('invalid user credentials') ||
    combined.includes('invalid username') ||
    combined.includes('invalid password')
  ) {
    return 'Invalid username or password.';
  }

  if (combined.includes('account disabled') || combined.includes('user disabled')) {
    return 'Your account is disabled. Please contact your March Health administrator.';
  }

  if (combined.includes('email not verified')) {
    return 'Your email is not verified. Please contact your March Health administrator.';
  }

  if (
    error === 'unauthorized_client' ||
    combined.includes('direct access grants') ||
    combined.includes('not allowed for direct access') ||
    combined.includes('client not allowed')
  ) {
    return 'Password sign-in is not enabled for this environment. Please contact your March Health administrator.';
  }

  if (error === 'invalid_client' || combined.includes('invalid client')) {
    return 'The sign-in client is misconfigured for this environment. Please contact your March Health administrator.';
  }

  if (error === 'invalid_request' || combined.includes('invalid redirect') || combined.includes('invalid scope')) {
    return 'The sign-in request is misconfigured for this environment. Please contact your March Health administrator.';
  }

  if (error === 'temporarily_unavailable' || status >= 500) {
    return 'Sign-in service is temporarily unavailable.';
  }

  if (status === 403) {
    return 'You do not have permission to access this resource.';
  }

  if (error) {
    return 'The sign-in service rejected the request. Please contact your March Health administrator.';
  }

  return 'Invalid username or password.';
}

export async function loginWithKeycloakPassword(
  username: string,
  password: string,
  sessionOptions?: LoginSessionOptions,
): Promise<PasswordLoginResult> {
  const issuer = getKeycloakIssuer();
  const clientId = getKeycloakClientId();
  if (!issuer || !clientId) {
    return {
      status: 'error',
      message: 'Set VITE_KEYCLOAK_ISSUER and VITE_KEYCLOAK_CLIENT_ID for development IAM login.',
    };
  }

  const trimmedUser = username.trim();
  if (!trimmedUser || !password) {
    return { status: 'error', message: 'Username and password are required.' };
  }

  const config = await getOidcConfiguration();
  const tokenUrl = config?.token_endpoint;
  if (!tokenUrl) {
    return { status: 'error', message: 'Keycloak token endpoint is not configured.' };
  }

  const body = new URLSearchParams({
    grant_type: 'password',
    client_id: clientId,
    username: trimmedUser,
    password,
    scope: 'openid profile email',
  });

  try {
    const res = await fetch(tokenUrl, {
      method: 'POST',
      // SEC-I5: ONLY in cookie mode. `credentials: 'include'` makes the browser
      // REQUIRE `Access-Control-Allow-Credentials: true` on the response, and
      // the server only sends that when cookie auth is on. Sending it
      // unconditionally made every cross-origin login fail with a CORS error --
      // the request reached the server and returned 200, the browser discarded
      // the response, and the UI reported "Invalid username or password".
      credentials: isCookieAuthMode() ? 'include' : 'same-origin',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: body.toString(),
    });
    const payload = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    if (!res.ok) {
      return { status: 'error', message: mapPasswordLoginError(payload, res.status) };
    }

    // SEC-I5: in cookie mode the tokens came back as httpOnly cookies and were
    // stripped from the body on purpose, so an absent access_token here is the
    // SUCCESS path. Without this branch, enabling cookie auth would make every
    // correct login report "temporarily unavailable".
    if (isCookieAuthMode()) {
      applyLoginSessionOptions({ ...sessionOptions, username: trimmedUser });
      markCookieSessionActive();
      return { status: 'success' };
    }

    const accessToken = String(payload.access_token ?? '').trim();
    if (!accessToken) {
      return { status: 'error', message: 'Sign-in is temporarily unavailable. Please try again.' };
    }

    const refreshToken = String(payload.refresh_token ?? '').trim();
    const expiresIn = Number(payload.expires_in ?? 0);
    const refreshExpiresIn = Number(payload.refresh_expires_in ?? 0);
    const expiresAt = expiresIn > 0 ? Date.now() + expiresIn * 1000 : undefined;
    const refreshExpiresAt =
      refreshExpiresIn > 0 ? Date.now() + refreshExpiresIn * 1000 : undefined;
    const claims = decodeJwtPayload(accessToken);

    setDevAuthSession(
      {
        accessToken,
        refreshToken: refreshToken || undefined,
        expiresAt,
        refreshExpiresAt,
        idToken: String(payload.id_token ?? '').trim() || undefined,
        tokenType: String(payload.token_type ?? 'Bearer').trim(),
        claims,
        staySignedIn: sessionOptions?.staySignedIn,
      },
      {
        ...sessionOptions,
        username: trimmedUser,
      },
    );

    return { status: 'success' };
  } catch {
    return {
      status: 'error',
      message: 'Unable to reach the sign-in service. Please try again.',
    };
  }
}
